import { NgSignaturePadOptions, SignaturePadComponent } from "@almothafar/angular-signature-pad";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { PermitStep, PersonRole, PhotoRequirement } from "@ha/data/basic";
import { BaseModalComponent, LanguageStateSelectors } from "@ha/ui/common";
import { Actions, ofActionCompleted, Select, Store } from "@ngxs/store";

import { TranslocoService } from "@ngneat/transloco";
import { untilDestroyed } from "@ngneat/until-destroy";
import { take } from "rxjs";
import { Observable } from "rxjs";
import { PermitPhoto } from "../../../permit-create/models/permit-photo.model";
import { PermitPhotoSelectors } from "../../../permit-create/states/permit-photo-selectors";
import { PermitPhotoActions } from "../../../permit-create/states/permit-photo.actions";
import { PermitCreateSignPersonModalComponentData } from "../../../shared/components/permit-create-sign-person-modal/permit-create-sign-person-modal.component";
import { PermitDetailsActions } from "../../states/permit-details.action";

@Component({
    selector: "pmt-permit-end-signature-modal",
    templateUrl: "./permit-end-signature-modal.component.html",
    styleUrls: ["./permit-end-signature-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitEndSignatureModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @ViewChild("signature")
    public signaturePad: SignaturePadComponent;

    @ViewChild("fileInput")
    public fileInput: ElementRef<HTMLInputElement>;

    @Select(PermitPhotoSelectors.permitEndPhotos)
    public photos$: Observable<PermitPhoto[]>;

    public photoRequirement = PhotoRequirement;
    public signaturePadOptions: NgSignaturePadOptions = undefined;
    public data: PermitCreateSignPersonModalComponentData;
    public hasSignature = false;
    public disabledFireAlarmRestored = false;
    public endPermitForm: FormGroup<{
        comment: FormControl<string | null>;
        photos: FormControl<PermitPhoto[] | null>;
    }>;
    public savePermitPhotoAction = PermitPhotoActions.SavePermitPhoto;
    public endPermitWithSignatureAction = PermitDetailsActions.EndPermitWithSignature;

    constructor(
        private store: Store,
        private cdr: ChangeDetectorRef,
        private actions$: Actions,
        private formBuilder: FormBuilder,
        private translocoService: TranslocoService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.actions$.pipe(ofActionCompleted(PermitDetailsActions.EndPermitWithSignature)).pipe(
            untilDestroyed(this),
            take(1),
        ).subscribe(() => {
            this.closeModal();
        });

        this.store.dispatch(new PermitPhotoActions.GetPermitPhotoRequirement());
        const currentLanguage = this.store.selectSnapshot(LanguageStateSelectors.currentLanguage);
        this.store.dispatch(new PermitPhotoActions.GetPermitPhotos(currentLanguage));

        this.initForm();
    }

    private initForm(): void {
        this.endPermitForm = this.formBuilder.group({
            comment: new FormControl<string | null>("", this.data.permitEndCommentRequired ? Validators.required : null),
            photos: new FormControl<PermitPhoto[] | null>(
                [],
                this.data.permitEndPhotoRequirement === PhotoRequirement.Required ? Validators.required : null,
            ),
        });

        // Subscribe to photos$ to update form control
        this.photos$.pipe(untilDestroyed(this)).subscribe((photos) => {
            this.endPermitForm.patchValue({ photos: photos || [] }, { emitEvent: false });
        });
    }

    public ngAfterViewInit(): void {
        this.getScreenSize();
        this.signaturePad.clear();
    }

    @HostListener("window:resize")
    public getScreenSize() {
        let width = 400;
        if (window.innerWidth < width) {
            width = window.innerWidth;
        }

        this.signaturePadOptions = {
            minWidth: 1,
            maxWidth: 4,
            backgroundColor: "#ffffff",
            canvasWidth: width,
            canvasHeight: width / 2,
        };

        this.cdr.detectChanges();
    }

    public get formattedRoles(): string {
        return this.data.roles.map((role) =>
            this.translocoService.translate("permit.signature.roles." + PersonRole[role].toLowerCase())
        ).join(", ");
    }

    public clear() {
        this.signaturePad.clear();
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    public async processSelectedImages(
        input: HTMLInputElement
        ): Promise<void> {

        const files = Array.from(input.files ?? []);

        for (const file of files) {
            const bytes = await file.arrayBuffer();

            const safeFile = new File([bytes], file.name, {
                type: file.type,
                lastModified: file.lastModified
            });

            this.store.dispatch(
            new PermitPhotoActions.SavePermitPhoto(
                this.data.permitId,
                safeFile,
                PermitStep.ended
            ));
        }

        input.value = '';
    }

    public removePhoto(guid: string): void {
        this.store.dispatch(new PermitPhotoActions.RemovePermitPhoto(guid)).pipe(
            untilDestroyed(this),
            take(1),
        ).subscribe(() => {
            // Clear the file input after successfully removing the photo
            if (this.fileInput && this.fileInput.nativeElement) {
                this.fileInput.nativeElement.value = "";
            }
        });
    }

    public get isValid(): boolean {
        const hasRequiredFireAlarmRestored = this.data.hasDisabledFireAlarm !== true || this.disabledFireAlarmRestored;
        return this.hasSignature && this.endPermitForm.valid && hasRequiredFireAlarmRestored;
    }

    public endPermit() {
        if (this.isValid) {
            const comment = this.endPermitForm.controls.comment.value ?? "";

            this.store.dispatch(
                new PermitDetailsActions.EndPermitWithSignature({
                    permitId: this.data.permitId,
                    personId: this.data.person.personId,
                    role: this.data.roles[0],
                    roles: undefined,
                    signatureData: this.signaturePad.toDataURL(),
                    endPostWatchSignature: false,
                    personalNumber: this.data.person.personalNumber,
                    hasForeignPersonalNumber: this.data.person.hasForeignPersonalNumber,
                    comment,
                }),
            );
        }
    }

    public drawComplete(event: MouseEvent | Touch): void {
        void event;
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    public drawStart(event: MouseEvent | Touch): void {
        void event;
        this.hasSignature = !this.signaturePad.isEmpty();
    }
}
